import { FC, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionSubmit,
	ActionText,
	AmountButton,
	GiverToggle,
	useActionInput,
} from '@bonusly/common-action-input';
import { CommonCard } from '@bonusly/common-give-box';
import { Box, Flex, Hide, rem, Spacer, StyleProps, useBreakpointValue } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { UserAvatar } from '../../components/user-avatar';
import { useCommentInput } from './hooks/use-comment-input';
import { SuggestedComments } from './suggested-comments';

export interface CommentComposerProps extends StyleProps {
	id?: string;
	bonus: Bonus;
	initalText?: string;
	onSuccess?: VoidFunction;
	commentBoxRef?: MutableRefObject<HTMLDivElement | null>;
}

const DynamicStyledActionText = () => {
	const { attachment, isLoadingAttachment } = useActionInput();
	return <ActionText inputStyles={{ minHeight: attachment || isLoadingAttachment ? undefined : rem(150) }} />;
};

export const CommentComposer: FC<CommentComposerProps> = ({ id, bonus, initalText, onSuccess, commentBoxRef }) => {
	const { t } = useTranslation('recognition');
	const user = useCurrentUser();
	const { isOwnRecognition, setInputText, ...config } = useCommentInput(bonus, {
		onSuccess,
	});

	const suggestedMessageTitle = useBreakpointValue<any>({
		base: 'bonus_input.quick_thank_you.mobile_title',
		sm: 'bonus_input.quick_thank_you.title',
	});
	const buttonLabel = 'bonus_input.quick_thank_you.generate';

	return (
		<CommonCard id={id} variant="inlay" ref={commentBoxRef}>
			<Flex columnGap={2}>
				<Hide below="sm">
					<Box>
						<UserAvatar size="md" user={user} />
					</Box>
				</Hide>

				<Box backgroundColor="bg.subtle" borderRadius="md" boxShadow="sm" p={3} w="full">
					<ActionInput autofocus="end" content={initalText} {...config}>
						<Flex alignItems="center" columnGap={2}>
							<Box>
								<AmountButton />
							</Box>
							<GiverToggle />
						</Flex>

						<Box mb={2}>
							<DynamicStyledActionText />
							{isOwnRecognition && <SuggestedComments bonus={bonus} onSetText={setInputText} title={t(suggestedMessageTitle)} buttonLabel={t(buttonLabel)} />}
							<ActionFeedback />
						</Box>

						<ActionMedia w="full" />

						<Flex
							alignItems={{ base: 'stretch', xs: 'flex-start' }}
							direction={{ base: 'column', xs: 'row' }}
							gap={2}
							id="comment-composer-submit-container"
							justifyContent="space-between"
							mt={4}
							w="full"
						>
							<ActionDecorations />
							<Spacer />

							<ActionSubmit size="lg" />
						</Flex>
					</ActionInput>
				</Box>
			</Flex>
		</CommonCard>
	);
};
